/**
 * Travel Assistant Service
 *
 * Facade Pattern: Provides a simplified interface to the travel assistant subsystem
 * Single Responsibility: Exposes public API functions for travel assistant operations
 */

import { TravelAssistantRepositoryFactory } from './api/factories/TravelAssistantRepositoryFactory';
import {
  OverviewRequest,
  OverviewResponse,
  QuickPeekRequest,
  QuickPeekResponse,
} from './api/interfaces/ITravelAssistantRepository';

/**
 * Fetch overview for a given location
 *
 * @param request - Overview request containing user_id, session_id, and location
 * @param accessToken - Optional access token for authentication
 * @param refreshToken - Optional refresh token for authentication
 * @returns Promise resolving to overview response
 */
/**
 * Fetch overview for a given location
 *
 * @param request - Overview request containing user_id, session_id, and location
 * @param accessToken - Optional access token for authentication
 * @param refreshToken - Optional refresh token for authentication
 * @returns Promise resolving to overview response
 */
export async function fetchOverview(
  request: OverviewRequest,
  accessToken?: string,
  refreshToken?: string,
  signal?: AbortSignal
): Promise<OverviewResponse> {
  const repository = TravelAssistantRepositoryFactory.getInstance();
  return repository.getOverview(request, accessToken, refreshToken, signal);
}

/**
 * Fetch quick peek travel planning information for a given location
 * @deprecated Use fetchQuickPeekTravelPlanningOnly and fetchQuickPeekNavigationAccessibility instead
 *
 * @param request - Quick peek request containing user_id, session_id, and location
 * @param accessToken - Optional access token for authentication
 * @param refreshToken - Optional refresh token for authentication
 * @returns Promise resolving to partial quick peek response (travel planning data only)
 */
export async function fetchQuickPeekTravelPlanning(
  request: QuickPeekRequest,
  accessToken?: string,
  refreshToken?: string,
  signal?: AbortSignal
): Promise<Partial<QuickPeekResponse>> {
  const repository = TravelAssistantRepositoryFactory.getInstance();
  return repository.getQuickPeekTravelPlanning(
    request,
    accessToken,
    refreshToken,
    signal
  );
}

/**
 * Fetch travel planning only information (travel modes, best time to visit, travel mode description)
 * Single Responsibility: Handles only travel planning data fetching
 *
 * @param request - Quick peek request containing user_id, session_id, and location
 * @param accessToken - Optional access token for authentication
 * @param refreshToken - Optional refresh token for authentication
 * @returns Promise resolving to partial quick peek response (travel planning only data)
 */
export async function fetchQuickPeekTravelPlanningOnly(
  request: QuickPeekRequest,
  accessToken?: string,
  refreshToken?: string,
  signal?: AbortSignal
): Promise<Partial<QuickPeekResponse>> {
  const repository = TravelAssistantRepositoryFactory.getInstance();
  return repository.getQuickPeekTravelPlanningOnly(
    request,
    accessToken,
    refreshToken,
    signal
  );
}

/**
 * Fetch navigation and accessibility information (accessibility, terrain types, navigation difficulty)
 * Single Responsibility: Handles only navigation/accessibility data fetching
 *
 * @param request - Quick peek request containing user_id, session_id, and location
 * @param accessToken - Optional access token for authentication
 * @param refreshToken - Optional refresh token for authentication
 * @returns Promise resolving to partial quick peek response (navigation/accessibility data)
 */
export async function fetchQuickPeekNavigationAccessibility(
  request: QuickPeekRequest,
  accessToken?: string,
  refreshToken?: string,
  signal?: AbortSignal
): Promise<Partial<QuickPeekResponse>> {
  const repository = TravelAssistantRepositoryFactory.getInstance();
  return repository.getQuickPeekNavigationAccessibility(
    request,
    accessToken,
    refreshToken,
    signal
  );
}

/**
 * Fetch quick peek cultural information for a given location
 * @deprecated Use fetchQuickPeekCulturalSocial and fetchQuickPeekHistoricalSouvenirs instead
 *
 * @param request - Quick peek request containing user_id, session_id, and location
 * @param accessToken - Optional access token for authentication
 * @param refreshToken - Optional refresh token for authentication
 * @returns Promise resolving to partial quick peek response (cultural info data only)
 */
export async function fetchQuickPeekCulturalInfo(
  request: QuickPeekRequest,
  accessToken?: string,
  refreshToken?: string,
  signal?: AbortSignal
): Promise<Partial<QuickPeekResponse>> {
  const repository = TravelAssistantRepositoryFactory.getInstance();
  return repository.getQuickPeekCulturalInfo(
    request,
    accessToken,
    refreshToken,
    signal
  );
}

/**
 * Fetch cultural and social information (cultural aspects, food specialties, local languages, local experiences)
 * Single Responsibility: Handles only cultural/social data fetching
 *
 * @param request - Quick peek request containing user_id, session_id, and location
 * @param accessToken - Optional access token for authentication
 * @param refreshToken - Optional refresh token for authentication
 * @returns Promise resolving to partial quick peek response (cultural/social data only)
 */
export async function fetchQuickPeekCulturalSocial(
  request: QuickPeekRequest,
  accessToken?: string,
  refreshToken?: string,
  signal?: AbortSignal
): Promise<Partial<QuickPeekResponse>> {
  const repository = TravelAssistantRepositoryFactory.getInstance();
  return repository.getQuickPeekCulturalSocial(
    request,
    accessToken,
    refreshToken,
    signal
  );
}

/**
 * Fetch historical significance and handicrafts/souvenirs information
 * Single Responsibility: Handles only historical/souvenirs data fetching
 *
 * @param request - Quick peek request containing user_id, session_id, and location
 * @param accessToken - Optional access token for authentication
 * @param refreshToken - Optional refresh token for authentication
 * @returns Promise resolving to partial quick peek response (historical/souvenirs data only)
 */
export async function fetchQuickPeekHistoricalSouvenirs(
  request: QuickPeekRequest,
  accessToken?: string,
  refreshToken?: string,
  signal?: AbortSignal
): Promise<Partial<QuickPeekResponse>> {
  const repository = TravelAssistantRepositoryFactory.getInstance();
  return repository.getQuickPeekHistoricalSouvenirs(
    request,
    accessToken,
    refreshToken,
    signal
  );
}

/**
 * Fetch quick peek activities and facilities information for a given location
 *
 * @param request - Quick peek request containing user_id, session_id, and location
 * @param accessToken - Optional access token for authentication
 * @param refreshToken - Optional refresh token for authentication
 * @returns Promise resolving to partial quick peek response (activities and facilities data only)
 */
export async function fetchQuickPeekActivitiesFacilities(
  request: QuickPeekRequest,
  accessToken?: string,
  refreshToken?: string,
  signal?: AbortSignal
): Promise<Partial<QuickPeekResponse>> {
  const repository = TravelAssistantRepositoryFactory.getInstance();
  return repository.getQuickPeekActivitiesFacilities(
    request,
    accessToken,
    refreshToken,
    signal
  );
}

/**
 * @deprecated Use fetchQuickPeekTravelPlanning, fetchQuickPeekCulturalInfo, and fetchQuickPeekActivitiesFacilities instead
 * Fetch quick peek information for a given location (merged from all 3 APIs)
 *
 * @param request - Quick peek request containing user_id, session_id, and location
 * @param accessToken - Optional access token for authentication
 * @param refreshToken - Optional refresh token for authentication
 * @returns Promise resolving to quick peek response
 */
export async function fetchQuickPeek(
  request: QuickPeekRequest,
  accessToken?: string,
  refreshToken?: string,
  signal?: AbortSignal
): Promise<QuickPeekResponse> {
  const repository = TravelAssistantRepositoryFactory.getInstance();
  return repository.getQuickPeek(request, accessToken, refreshToken, signal);
}
